import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import Select from 'components/inputs/select';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useState } from 'react';
import axios from 'utils/api';
import { errorModal, successModal } from 'utils/ui';

type LevelSettings = Record<
  string,
  Record<number, { primaryValue: string; secondaryValue: string | null }>
>;

type ColumnDef = {
  header: string;
  category: string;
  field: 'primaryValue' | 'secondaryValue';
  options: string[];
};

const COLUMNS: ColumnDef[] = [
  {
    header: 'Wall Cavity Insulation',
    category: 'walls',
    field: 'primaryValue',
    options: ['None', 'R-11', 'R-13', 'R-15', 'R-19', 'R-21']
  },
  {
    header: 'Wall Board Insulation',
    category: 'walls',
    field: 'secondaryValue',
    options: ['None', 'R-2', 'R-3', 'R-4', 'R-5', 'R-6']
  },
  {
    header: 'Ceiling Below Attic',
    category: 'attic',
    field: 'primaryValue',
    options: [
      'None',
      'R-7',
      'R-11',
      'R-13',
      'R-15',
      'R-19',
      'R-21',
      'R-25',
      'R-28',
      'R-30',
      'R-38',
      'R-44',
      'R-50',
      'R-56'
    ]
  },
  {
    header: 'Ceiling Below Roof Joists',
    category: 'ceilingBelowRoof',
    field: 'primaryValue',
    options: [
      'None',
      'R-2',
      'R-3',
      'R-4',
      'R-5',
      'R-6',
      'R-8',
      'R-10',
      'R-11',
      'R-13',
      'R-15',
      'R-19',
      'R-21',
      'R-30',
      'R-38'
    ]
  },
  {
    header: 'Ceiling On Exposed Beams',
    category: 'ceilingBelowRoof',
    field: 'secondaryValue',
    options: [
      'None',
      'R-2',
      'R-3',
      'R-4',
      'R-5',
      'R-6',
      'R-7',
      'R-8',
      'R-10',
      'R-12',
      'R-15',
      'R-17',
      'R-20',
      'R-22',
      'R-25',
      'R-28',
      'R-31',
      'R-34'
    ]
  },
  {
    header: 'Floor Over Unconditioned Space',
    category: 'floor',
    field: 'primaryValue',
    options: [
      'None',
      'R-2',
      'R-4',
      'R-5',
      'R-7',
      'R-9',
      'R-11',
      'R-13',
      'R-15',
      'R-19',
      'R-21',
      'R-30'
    ]
  },
  {
    header: 'Floor Over Exposed Space',
    category: 'floor',
    field: 'secondaryValue',
    options: [
      'None',
      'R-2',
      'R-3',
      'R-4',
      'R-5',
      'R-6',
      'R-7',
      'R-8',
      'R-9',
      'R-10',
      'R-11',
      'R-13',
      'R-15',
      'R-19',
      'R-21',
      'R-30',
      'R-38'
    ]
  }
];

const LEVELS = [1, 2, 3, 4, 5];

export default function InsulationLevelsPage() {
  const dispatch = useAppDispatch();
  const { insulationLevelSettings } = useAppSelector((state) => state.admin);
  const [localData, setLocalData] = useState<LevelSettings | null>(null);

  useEffect(() => {
    if (insulationLevelSettings) {
      setLocalData(JSON.parse(JSON.stringify(insulationLevelSettings)));
    }
  }, [insulationLevelSettings]);

  const updateCell = (
    category: string,
    level: number,
    field: 'primaryValue' | 'secondaryValue',
    value: string
  ) => {
    setLocalData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [category]: {
          ...prev[category],
          [level]: {
            ...prev[category]?.[level],
            [field]: value
          }
        }
      };
    });
  };

  const getCellValue = (
    category: string,
    level: number,
    field: 'primaryValue' | 'secondaryValue'
  ): string => {
    return localData?.[category]?.[level]?.[field] || 'None';
  };

  const hasChanges =
    JSON.stringify(localData) !== JSON.stringify(insulationLevelSettings);

  const handleSave = async () => {
    if (!localData) return;
    try {
      await axios.patch('sizing/insulation-level-settings', localData);
      dispatch(fetchDefaultAdminData());
      successModal('Insulation level settings updated successfully.');
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const handleReset = () => {
    if (insulationLevelSettings) {
      setLocalData(JSON.parse(JSON.stringify(insulationLevelSettings)));
    }
  };

  if (!localData) {
    return (
      <div className='py-8 text-center text-gray-500'>
        Loading insulation level settings...
      </div>
    );
  }

  return (
    <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
      <div className='text-3xl font-bold text-gray-900'>
        Insulation Level Settings
      </div>
      <div className='flex items-center gap-3'>
        <Button
          text='Save'
          color='green'
          onClick={handleSave}
          disabled={!hasChanges}
        />
        <Button
          text='Reset'
          color='red'
          onClick={handleReset}
          disabled={!hasChanges}
        />
      </div>
      <div className='overflow-x-auto'>
        <table className='w-full border-collapse'>
          <thead>
            <tr>
              <th className='border border-gray-300 bg-gray-100 px-4 py-2 text-left text-sm font-semibold text-gray-700'>
                Level
              </th>
              {COLUMNS.map((col) => (
                <th
                  key={`${col.category}-${col.field}`}
                  className='border border-gray-300 bg-gray-100 px-4 py-2 text-left text-sm font-semibold text-gray-700'
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {LEVELS.map((level) => (
              <tr key={level} className='bg-white hover:bg-gray-50'>
                <td className='border border-gray-300 px-4 py-2 text-center text-lg font-bold text-gray-800'>
                  {level}
                </td>
                {COLUMNS.map((col) => (
                  <td
                    key={`${col.category}-${col.field}-${level}`}
                    className='border border-gray-300 px-2 py-1'
                  >
                    <Select
                      value={getCellValue(col.category, level, col.field)}
                      options={col.options}
                      onChange={(val) =>
                        updateCell(col.category, level, col.field, String(val))
                      }
                      size='sm'
                      preventDeselection
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
